import {
  DEFAULT_FETCHING_NEWS_OPTIONS,
  fetchNewsArticles,
  useFetchNewsArticles,
  useFetchNewsArticlesCount,
} from "@/apis/queryFunctions/news";
import { newsKeys } from "@/apis/queryKeys";
import BannerTop from "@/common/components/BannerTop";
import AppBreadcrumbs from "@/common/components/Breadcrumbs";
import DefaultSEO from "@/common/components/DefaultSEO";
import Container from "@/common/Layout/Container/Container";
import NewCard from "@/common/NewCard";
import TitlePage from "@/common/Typography/TitlePage";
import { Box, Center, Grid, Pagination, Text } from "@mantine/core";
import { HiOutlineEmojiSad } from "@react-icons/all-files/hi/HiOutlineEmojiSad";
import { dehydrate, QueryClient } from "@tanstack/react-query";
import Head from "next/head";
import { useRouter } from "next/router";
import React from "react";

export default function NewsPage() {
  const { locale } = useRouter();
  const breadcrumbs = [
    {
      title: locale === "vi" ? "Trang chủ" : "Home",
      href: "/",
    },
    {
      title: locale === "vi" ? "Tin tức" : "News",
      href: "/tin-tuc",
    },
  ];
  const limit = process.env.NEWS_LOADING_LIMIT;
  const [page, setPage] = React.useState(1);
  const [options, setOptions] = React.useState({
    limit: process.env.NEWS_LOADING_LIMIT,
    page: 1,
    condition: {
      ngon_ngu: locale,
      loai_tin_tuc: "Tin tức cà phê",
    },
  });
  const { data: news } = useFetchNewsArticles(options);
  const { data: newsCount } = useFetchNewsArticlesCount(
    {
      limit: process.env.NEWS_LOADING_LIMIT,
      page: 1,
      collection_name: "news",
      condition: {
        ngon_ngu: locale,
        loai_tin_tuc: "Tin tức cà phê",
      },
    },
    locale
  );

  React.useEffect(() => {
    setOptions((prevOps) => ({
      ...prevOps,
      page,
      condition: {
        ...prevOps?.condition,
        ngon_ngu: locale,
      },
    }));
  }, [page, locale]);

  const seo = {
    title: "Tin tức",
  };

  return (
    <div>
      <DefaultSEO seo={seo} />
      {/* <BannerTop position="tin-tuc" aspectRatio="2.44" /> */}
      <Container>
        <Box py={20}>
          <AppBreadcrumbs items={breadcrumbs} isLoading={!breadcrumbs} />
          <TitlePage>{locale === "vi" ? "Tin tức" : "News"}</TitlePage>
          <Box mb={20}>
            <Grid>
              {news
                ? news.slice(0, limit).map((item) => (
                    <Grid.Col xs={12} sm={6} lg={3} key={item._id}>
                      <NewCard item={item} />
                    </Grid.Col>
                  ))
                : Array(limit)
                    .fill(undefined)
                    .map((item, i) => (
                      <Grid.Col xs={12} sm={6} lg={3} key={i}>
                        <NewCard item={item} isLoading />
                      </Grid.Col>
                    ))}
            </Grid>
          </Box>
          {!!newsCount > 0 && limit ? (
            <Center>
              <Pagination
                page={page}
                onChange={setPage}
                total={Math.ceil(newsCount / limit)}
                size="xs"
              />
            </Center>
          ) : (
            <Box
              sx={(theme) => ({
                width: "100%",
                height: "100%",
                display: "flex",
                flexGrow: 1,
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                paddingTop: 40,
                paddingBottom: 40,
              })}
            >
              <HiOutlineEmojiSad size={32} />
              <Text align="center">
                {locale === "vi"
                  ? "Không có tin tức tồn tại"
                  : " No news exists"}
              </Text>
            </Box>
          )}
        </Box>
      </Container>
    </div>
  );
}

export async function getStaticProps({ locale }) {
  const queryClient = new QueryClient();
  const newsOptions = {
    ...DEFAULT_FETCHING_NEWS_OPTIONS,
    condition: {
      ...DEFAULT_FETCHING_NEWS_OPTIONS.condition,
      ngon_ngu: locale,
    },
  };

  await queryClient.prefetchQuery(newsKeys.list(newsOptions), () =>
    fetchNewsArticles(newsOptions)
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 60,
  };
}
